import React, { useState, useEffect } from "react";
import { Table, Input, Button, Space, Popconfirm, Tag, message, Typography } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ShareAltOutlined, ReloadOutlined, LinkOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import CouponServ from "../../../service/CouponServ";
import { formatDate } from "../../../util/dateUtils";
import GlobalTooltip from "../../../component/GlobalTooltip";
import { FilterBar } from "../../../component/ui/FilterBar";
import SearchInput from "../../../component/ui/SearchInput";
import { PrimaryButton, ResetButton } from "../../../component/ui/ActionButtons";
import DataTable from "../../../component/ui/DataTable";
import TableActions from "../../../component/ui/TableActions";
import { usePreference } from "../../../context/PreferenceContext";
import CouponDashboard from "./CouponDashboard";

const { Text } = Typography;

const CouponList = () => {
    const { formatPrice } = usePreference();
    const navigate = useNavigate();
    
    const [searchText, setSearchText] = useState("");
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCoupons, setTotalCoupons] = useState(0);

    const fetchCoupons = async (p = page) => {
        try {
            setLoading(true);
            const params = {
                limit: pageSize,
                skip: (p - 1) * pageSize,
                sort: JSON.stringify({ createdAt: -1 }),
                search: searchText
            };
            const response = await CouponServ.index(params);
            setCoupons(Array.isArray(response?.data) ? response?.data : []);
            setTotalCoupons(response?.total || 0);
        } catch (error) {
            console.error("Error fetching coupons:", error);
            message.error("Failed to fetch coupons");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCoupons();
    }, [page, pageSize, searchText]);

    const handleToggleStatus = async (record) => {
        try {
            await CouponServ.update(record._id, { isActive: !record.isActive });
            message.success(`Coupon ${!record.isActive ? 'activated' : 'deactivated'} successfully`);
            fetchCoupons();
        } catch (error) {
            message.error("Failed to update status");
        }
    };

    const handleDelete = async (record) => {
        try {
            await CouponServ.remove(record._id);
            message.success("Coupon removed successfully");
            fetchCoupons();
        } catch (error) {
            message.error("Failed to remove coupon");
        }
    };

    const handleShare = (record) => {
        const shareData = {
            title: 'Nearzon Coupon',
            text: `Use coupon code "${record.code}" to get ${record.discountType === 'percentage' ? record.discountValue + '%' : '₹' + record.discountValue} off on Nearzon!`,
            url: window.location.origin
        };

        if (navigator.share) {
            navigator.share(shareData)
                .then(() => message.success("Coupon shared successfully!"))
                .catch((error) => console.error('Error sharing', error));
        } else {
            navigator.clipboard.writeText(record.code);
            message.success("Coupon code copied to clipboard! Share it with your buyers.");
        }
    };

    const fetchAllCoupons = async () => {
        try {
            const res = await CouponServ.index({ limit: 1000 });
            return Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        } catch (error) {
            return [];
        }
    };

    const columns = [
        {
            title: "Code",
            dataIndex: "code",
            key: "code",
            render: (code, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ 
                        background: '#f0f7ff', 
                        padding: '4px 12px', 
                        borderRadius: 8, 
                        border: '1.5px dashed #4776E6',
                    }}>
                        <Text strong style={{ color: '#4776E6', letterSpacing: 1, fontSize: '13px' }}>{code}</Text>
                    </div>
                </div>
            ),
        },
        {
            title: "Type",
            dataIndex: "couponType",
            key: "couponType",
            render: (type) => {
                let displayText = 'GENERAL';
                if (type === 'welcome') displayText = 'FIRST ORDER';
                else if (type === 'free_delivery') displayText = 'FREE DELIVERY';
                else if (type === 'retention') displayText = 'RETENTION';
                else if (type) displayText = type.replace(/_/g, ' ').toUpperCase();
                
                return (
                    <Tag color={type === 'welcome' ? 'magenta' : type === 'free_delivery' ? 'cyan' : 'blue'}>
                        {displayText}
                    </Tag>
                );
            }
        },
        {
            title: "Discount",
            key: "discount",
            render: (_, record) => (
                <div>
                    <span style={{ 
                        color: '#722ed1', 
                        background: '#f9f0ff', 
                        padding: '4px 10px', 
                        borderRadius: '6px', 
                        fontWeight: 800, 
                        fontSize: '12px',
                        border: '1px solid #efdbff'
                    }}>
                        {record.discountType === 'percentage' ? `${record.discountValue}% OFF` : `${formatPrice(record.discountValue)} OFF`}
                    </span>
                    {record.freeDelivery && <div style={{ fontSize: '11px', color: '#10b981', marginTop: 4, fontWeight: 700 }}>+ FREE DELIVERY</div>}
                </div>
            ),
        },
        {
            title: "Min Order",
            dataIndex: "minOrderAmount",
            key: "minOrderAmount",
            render: (val) => <Text strong style={{ color: '#1e293b', fontSize: '13px' }}>{formatPrice(val)}</Text>,
        },
        {
            title: "Usage Status",
            key: "usage",
            render: (_, record) => (
                <div>
                    <div style={{ fontSize: '13px', fontWeight: 700 }}>
                        <span style={{ color: '#1e293b' }}>{record.usedCount || 0}</span>
                        <span style={{ color: '#cbd5e1', margin: '0 4px' }}>/</span>
                        <span style={{ color: '#64748b' }}>{record.usageLimit || '∞'}</span>
                    </div>
                </div>
            ),
        },
        {
            title: "Status",
            dataIndex: "isActive",
            key: "isActive",
            align: 'center',
            render: (isActive, record) => {
                const now = new Date();
                const isExpired = record.endDate && new Date(record.endDate) < now;
                const isLimitReached = record.usageLimit && record.usedCount >= record.usageLimit;

                if (isExpired) {
                    return (
                        <span style={{
                            borderRadius: '8px',
                            padding: '4px 12px',
                            background: '#fff7ed',
                            color: '#ea580c',
                            border: '1px solid #fed7aa',
                            fontSize: '11px',
                            fontWeight: 800,
                        }}>
                            EXPIRED
                        </span>
                    );
                }

                if (isLimitReached) {
                    return (
                        <span style={{
                            borderRadius: '8px',
                            padding: '4px 12px',
                            background: '#fef2f2',
                            color: '#dc2626',
                            border: '1px solid #fecaca',
                            fontSize: '11px',
                            fontWeight: 800,
                        }}>
                            LIMIT REACHED
                        </span>
                    );
                }

                return (
                    <span
                        style={{
                            cursor: 'pointer',
                            borderRadius: '8px',
                            padding: '4px 12px',
                            background: isActive ? '#f0fdf4' : '#f8fafc',
                            color: isActive ? '#22c55e' : '#94a3b8',
                            border: `1px solid ${isActive ? '#dcfce7' : '#f1f5f9'}`,
                            fontSize: '11px',
                            fontWeight: 800,
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleToggleStatus(record);
                        }}
                    >
                        {isActive ? "ACTIVE" : "INACTIVE"}
                    </span>
                );
            },
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            width: 170,
            render: (_, record) => (
                <Space>
                    <Button 
                        type="primary" 
                        size="small" 
                        onClick={() => navigate(`/admin/coupons/details/${record._id}`)}
                        style={{ background: '#fff', color: '#004754', border: '1px solid #e4e4e7', fontSize: 13 }}
                    >
                        View
                    </Button>
                    <GlobalTooltip title="Edit">
                        <Button 
                            type="link" 
                            size="small"
                            icon={<EditOutlined />} 
                            onClick={() => navigate(`/admin/coupons/edit/${record._id}`)}
                        />
                    </GlobalTooltip>
                    <GlobalTooltip title="Share">
                        <Button 
                            type="link" 
                            size="small"
                            icon={<ShareAltOutlined style={{ color: '#10b981' }} />} 
                            onClick={() => handleShare(record)}
                        />
                    </GlobalTooltip>
                    <Popconfirm title="Remove this coupon?" onConfirm={() => handleDelete(record)}>
                        <GlobalTooltip title="Delete">
                            <Button type="link" danger size="small" icon={<DeleteOutlined />} />
                        </GlobalTooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const topRef = React.useRef(null);

    return (
        <div ref={topRef}>
            <CouponDashboard />
            <div style={{ background: '#fff', padding: '24px', borderRadius: '12px', border: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <FilterBar>
                    <SearchInput
                        label="Search Coupons"
                        placeholder="Search by code..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </FilterBar>
                
                <div style={{ display: 'flex', gap: 12 }}>
                    <TableActions 
                        onFetchFullData={fetchAllCoupons} 
                        columns={columns} 
                        fileName="coupons" 
                        pageName="Coupons" 
                    />
                    <PrimaryButton
                        icon={<PlusOutlined />}
                        text="Add Coupon"
                        onClick={() => navigate("/admin/coupons/create")}
                    />
                </div>
            </div>

            <DataTable
                data={coupons}
                columns={columns}
                rowKey="_id"
                loading={loading}
                pagination={{
                    current: page,
                    pageSize: pageSize,
                    total: totalCoupons,
                    onChange: (p, ps) => { 
                        setPage(p); 
                        setPageSize(ps);
                        topRef.current?.scrollIntoView({ behavior: 'smooth' });
                    },
                    showSizeChanger: true,
                }}
            />
        </div>
        </div>
    );
};

export default CouponList;
